import type { RouteLdFlagName } from './launch-darkly'
import type { FeatureFlagName } from '@omnea-app/fe-org-settings/feature-flags/types'

/**
 * A rule names its flag rather than reading it, so it stays evaluable outside React.
 *
 * The two flag arms differ in whether a value can be unsettled, not in which vendor serves it: an
 * organisation flag resolves with the session, so only `ldFlag` can answer `pending`.
 */
export type AvailabilityRule =
  | { kind: 'always' }
  | { kind: 'orgFlag'; name: FeatureFlagName }
  | { kind: 'ldFlag'; name: RouteLdFlagName }
  | { kind: 'omneanOnly' }
  | { kind: 'any'; rules: readonly AvailabilityRule[] }
  | { kind: 'all'; rules: readonly AvailabilityRule[] }

/**
 * Whether a route exists for this caller at all — the organisation's entitlement, the rollout, and the
 * audience a route is built for.
 *
 * `pending` is a source that has not resolved yet. It is distinct from `unavailable`: an unresolved
 * flag reads `false`, and treating that as a denial hides a route from an organisation that has it.
 */
export type Availability = 'available' | 'unavailable' | 'pending'

export type AvailabilityInputs = {
  orgFlags: Readonly<Partial<Record<FeatureFlagName, boolean>>>
  isLdReady: boolean
  ldFlags: Readonly<Partial<Record<RouteLdFlagName, boolean>>>
  isOmnean: boolean
}

export const always = (): AvailabilityRule => ({ kind: 'always' })

export const orgFlag = (name: FeatureFlagName): AvailabilityRule => ({ kind: 'orgFlag', name })

export const ldFlag = (name: RouteLdFlagName): AvailabilityRule => ({ kind: 'ldFlag', name })

/** Internal tooling, which the Permissions Service cannot answer for: it knows grants, not who Omnea employs. */
export const omneanOnly = (): AvailabilityRule => ({ kind: 'omneanOnly' })

export const any = (rules: readonly AvailabilityRule[]): AvailabilityRule => ({ kind: 'any', rules })

export const all = (rules: readonly AvailabilityRule[]): AvailabilityRule => ({ kind: 'all', rules })
